import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shell } from "@/pages/Auth";
import { api, formatError } from "@/lib/api";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (e2) { setErr(formatError(e2)); }
    setBusy(false);
  };
  return (
    <Shell title="Forgot your password?" subtitle="Enter your account email and we'll send you a link to reset it.">
      {sent ? (
        <div data-testid="forgot-success" className="flex flex-col items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">
          <MailCheck className="h-6 w-6" />
          <p>If an account exists for <b>{email}</b>, a reset link is on its way. It expires in 1 hour — check your spam folder too.</p>
          <Link to="/login" data-testid="forgot-back-login" className="font-semibold text-aqua hover:underline">Back to login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-5" data-testid="forgot-form">
          <div className="flex flex-col gap-2"><Label htmlFor="femail">Email</Label>
            <Input id="femail" data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} /></div>
          {err && <p data-testid="forgot-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
          <Button data-testid="forgot-submit-btn" type="submit" disabled={busy} className="h-11 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}</Button>
          <Link to="/login" className="text-sm font-semibold text-aqua hover:underline">Back to login</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ a: "", b: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.a.length < 8) return setErr("Password must be at least 8 characters.");
    if (pw.a !== pw.b) return setErr("Passwords don't match.");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, password: pw.a }); setDone(true); setTimeout(() => nav("/login"), 2500); } catch (e2) { setErr(formatError(e2)); }
    setBusy(false);
  };
  if (!token) {
    return <Shell title="Invalid link" subtitle="This reset link is missing its token."><Link to="/forgot-password" className="font-semibold text-aqua hover:underline">Request a new link</Link></Shell>;
  }
  return (
    <Shell title="Choose a new password" subtitle="Use at least 8 characters.">
      {done ? (
        <p data-testid="reset-success" className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 text-sm text-emerald-900">Password updated. Redirecting you to login...</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-5" data-testid="reset-form">
          <div className="flex flex-col gap-2"><Label htmlFor="npw">New password</Label>
            <Input id="npw" data-testid="reset-password-input" type="password" required value={pw.a} onChange={(e) => setPw({ ...pw, a: e.target.value })} /></div>
          <div className="flex flex-col gap-2"><Label htmlFor="npw2">Confirm new password</Label>
            <Input id="npw2" data-testid="reset-confirm-input" type="password" required value={pw.b} onChange={(e) => setPw({ ...pw, b: e.target.value })} /></div>
          {err && <p data-testid="reset-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{err} {err.includes("expired") && <Link to="/forgot-password" className="font-semibold underline">Request a new link</Link>}</p>}
          <Button data-testid="reset-submit-btn" type="submit" disabled={busy} className="h-11 rounded-full">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}</Button>
        </form>
      )}
    </Shell>
  );
}
